import { useState, useCallback } from 'react';
import { useCreateEvent, useEventTypes } from '../../hooks/useEvents';
import { useVenues } from '../../hooks/useVenues';

interface CreateEventPanelProps {
  onClose: () => void;
  onCreated: () => void;
}

interface FormState {
  name: string;
  eventTypeId: string;
  /** datetime-local input value, e.g. "2026-10-05T18:00". Browser-local. */
  startAt: string;
  /** datetime-local input value (optional). */
  endAt: string;
  venueId: string;
  guestCount: string;
  budget: string;
  notes: string;
}

const initialForm: FormState = {
  name: '',
  eventTypeId: '',
  startAt: '',
  endAt: '',
  venueId: '',
  guestCount: '',
  budget: '',
  notes: '',
};

export function CreateEventPanel({ onClose, onCreated }: CreateEventPanelProps) {
  const createEvent = useCreateEvent();
  const { data: eventTypes = [] } = useEventTypes();
  const { data: venues = [] } = useVenues({ isActive: true });
  const [form, setForm] = useState<FormState>(initialForm);
  const [error, setError] = useState('');

  const isValid =
    form.name.trim() !== '' && form.eventTypeId.trim() !== '' && form.startAt.trim() !== '';

  const handleCreate = useCallback(() => {
    setError('');
    // datetime-local strings are in browser-local time; `new Date(str)` parses
    // them as local and serializes to UTC for the JSON API. Server stores UTC.
    const startAt = new Date(form.startAt);
    const endAt = form.endAt ? new Date(form.endAt) : undefined;
    if (endAt && endAt < startAt) {
      setError('End must be on or after start');
      return;
    }
    createEvent.mutate(
      {
        name: form.name.trim(),
        eventTypeId: form.eventTypeId,
        startAt,
        ...(endAt && { endAt }),
        venueId: form.venueId || undefined,
        guestCount: form.guestCount ? parseInt(form.guestCount, 10) : undefined,
        budget: form.budget ? parseFloat(form.budget) : undefined,
        notes: form.notes.trim() || undefined,
      },
      {
        onSuccess: () => {
          onCreated();
        },
        onError: (err: unknown) => {
          const message =
            (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
            'Failed to create event';
          setError(message);
        },
      },
    );
  }, [form, createEvent, onCreated]);

  return (
    <div className="md:border-l border-gray-200 p-4 md:p-6 bg-white overflow-auto">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-lg font-semibold">Create Event</h2>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 text-xl leading-none"
          aria-label="Close panel"
        >
          &times;
        </button>
      </div>

      {error && (
        <div className="mb-4 px-3 py-2 bg-red-50 text-red-700 text-sm rounded">{error}</div>
      )}

      <div className="space-y-4">
        <div>
          <label htmlFor="create-name" className="block text-sm font-medium text-gray-700 mb-1">
            Name
          </label>
          <input
            id="create-name"
            type="text"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            placeholder="Event name"
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>

        <div>
          <label
            htmlFor="create-event-type"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Event Type
          </label>
          <select
            id="create-event-type"
            value={form.eventTypeId}
            onChange={(e) => setForm((f) => ({ ...f, eventTypeId: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="">Select event type</option>
            {eventTypes.map((type) => (
              <option key={type.id} value={type.id}>
                {type.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="create-start-at" className="block text-sm font-medium text-gray-700 mb-1">
            Start
          </label>
          <input
            id="create-start-at"
            type="datetime-local"
            value={form.startAt}
            onChange={(e) => setForm((f) => ({ ...f, startAt: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>

        <div>
          <label htmlFor="create-end-at" className="block text-sm font-medium text-gray-700 mb-1">
            End <span className="text-xs text-gray-400 font-normal">(optional)</span>
          </label>
          <input
            id="create-end-at"
            type="datetime-local"
            value={form.endAt}
            onChange={(e) => setForm((f) => ({ ...f, endAt: e.target.value }))}
            min={form.startAt || undefined}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>

        <div>
          <label htmlFor="create-venue" className="block text-sm font-medium text-gray-700 mb-1">
            Venue
          </label>
          <select
            id="create-venue"
            value={form.venueId}
            onChange={(e) => setForm((f) => ({ ...f, venueId: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="">Select venue (optional)</option>
            {venues.map((venue) => (
              <option key={venue.id} value={venue.id}>
                {venue.name}
                {venue.city ? ` — ${venue.city}` : ''}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label
            htmlFor="create-guest-count"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Guest Count
          </label>
          <input
            id="create-guest-count"
            type="number"
            value={form.guestCount}
            onChange={(e) => setForm((f) => ({ ...f, guestCount: e.target.value }))}
            placeholder="Optional"
            min="1"
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>

        <div>
          <label htmlFor="create-budget" className="block text-sm font-medium text-gray-700 mb-1">
            Budget
          </label>
          <input
            id="create-budget"
            type="number"
            value={form.budget}
            onChange={(e) => setForm((f) => ({ ...f, budget: e.target.value }))}
            placeholder="Optional"
            min="0"
            step="0.01"
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>

        <div>
          <label htmlFor="create-notes" className="block text-sm font-medium text-gray-700 mb-1">
            Notes
          </label>
          <textarea
            id="create-notes"
            value={form.notes}
            onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
            placeholder="Optional"
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
          />
        </div>
      </div>

      <div className="mt-6 flex gap-3">
        <button
          onClick={handleCreate}
          disabled={!isValid || createEvent.isPending}
          className="px-4 py-2 bg-primary-600 text-white text-sm rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {createEvent.isPending ? 'Creating...' : 'Create Event'}
        </button>
        <button
          onClick={onClose}
          className="px-4 py-2 border border-gray-300 text-gray-700 text-sm rounded-md hover:bg-gray-50"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
